/**
 * Tiny client-side error beacon.
 *
 * - Installs global `window.onerror` + `unhandledrejection` handlers.
 * - Wraps `console.error` to also beacon (without breaking the console output).
 * - Deduplicates identical errors within a rolling window.
 * - Uses `navigator.sendBeacon` (fires-and-forgets even on page unload) with a
 *   `fetch(keepalive: true)` fallback.
 * - Auth is optional server-side, so pre-login errors are still captured. When a
 *   token exists in localStorage.gg_token we attach it as a Bearer header.
 *
 * NEVER throws — telemetry failure must not create more telemetry.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${BACKEND_URL}/api/telemetry/js-error`;

// dedupe cache: fingerprint -> last-reported ms
const seen = new Map();
const DEDUPE_MS = 30_000;
// hard cap so a doom-looping bug can't flood the backend
let sentInSession = 0;
const MAX_PER_SESSION = 50;

// Compact string hash → 8-char hex (djb2)
function fingerprint(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
    return h.toString(16).padStart(8, "0");
}

function isDupe(fp) {
    const now = Date.now();
    const last = seen.get(fp);
    if (last && now - last < DEDUPE_MS) return true;
    seen.set(fp, now);
    // Keep the map small
    if (seen.size > 200) {
        const oldest = [...seen.entries()].sort((a, b) => a[1] - b[1])[0];
        if (oldest) seen.delete(oldest[0]);
    }
    return false;
}

function beacon(payload) {
    if (sentInSession >= MAX_PER_SESSION) return;
    if (!BACKEND_URL) return; // no config, no send
    sentInSession += 1;
    const body = JSON.stringify(payload);
    try {
        // Prefer sendBeacon — non-blocking, survives page unload. Doesn't allow custom headers,
        // so we only use it for unauth'd or when explicit auth isn't needed.
        const token = localStorage.getItem("gg_token");
        if (!token && "sendBeacon" in navigator) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(ENDPOINT, blob);
            return;
        }
        // Otherwise use fetch with keepalive so it fires even during unload
        fetch(ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body,
            keepalive: true,
        }).catch(() => { /* eat — never re-throw from telemetry */ });
    } catch { /* eat */ }
}

function makePayload({ message, stack, source, line, column, kind }) {
    const url = typeof window !== "undefined" && window.location ? window.location.href : "";
    const ua  = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const fp  = fingerprint(`${message}|${source || ""}|${line || 0}`);
    return {
        message: (message || "").toString().slice(0, 2000),
        stack: (stack || "").toString().slice(0, 8000),
        source: (source || "").toString().slice(0, 800),
        line: typeof line === "number" ? line : null,
        column: typeof column === "number" ? column : null,
        url: url.slice(0, 800),
        user_agent: ua.slice(0, 800),
        kind: kind || "error",
        fingerprint: fp,
        client_ts: new Date().toISOString(),
    };
}

let installed = false;

export function installClientErrorBeacon() {
    if (installed || typeof window === "undefined") return;
    installed = true;

    // 1. Uncaught runtime errors
    window.addEventListener("error", (ev) => {
        try {
            const err = ev.error;
            const payload = makePayload({
                message: ev.message || (err && err.message) || "Unknown error",
                stack: err && err.stack ? String(err.stack) : "",
                source: ev.filename || "",
                line: ev.lineno,
                column: ev.colno,
                kind: "error",
            });
            if (isDupe(payload.fingerprint)) return;
            beacon(payload);
        } catch { /* eat */ }
    });

    // 2. Unhandled promise rejections
    window.addEventListener("unhandledrejection", (ev) => {
        try {
            const reason = ev.reason;
            const message =
                (reason && (reason.message || (typeof reason === "string" ? reason : ""))) ||
                "Unhandled promise rejection";
            const payload = makePayload({
                message,
                stack: reason && reason.stack ? String(reason.stack) : "",
                kind: "unhandled_promise",
            });
            if (isDupe(payload.fingerprint)) return;
            beacon(payload);
        } catch { /* eat */ }
    });

    // 3. Wrap console.error — keep the original behavior, but also send.
    //    Explicit opt-out via console.error({ __gg_no_beacon: true }, ...) so
    //    the beacon's OWN error path can never recurse.
    const origErr = console.error.bind(console);
    console.error = function ggErrorProxy(...args) {
        try {
            origErr(...args);
            const firstArg = args[0];
            if (firstArg && typeof firstArg === "object" && firstArg.__gg_no_beacon) return;
            const parts = args.map((a) => {
                if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`;
                if (typeof a === "string") return a;
                try { return JSON.stringify(a); } catch { return String(a); }
            });
            const message = parts.join(" ").slice(0, 2000);
            const payload = makePayload({
                message,
                stack: args.find((a) => a instanceof Error)?.stack || "",
                kind: "console",
            });
            if (isDupe(payload.fingerprint)) return;
            beacon(payload);
        } catch { /* eat */ }
    };
}

/** Test helper — send a synthetic error. Only used in dev/testing. */
export function _debug_forceBeacon(message = "synthetic-beacon-test") {
    beacon(makePayload({ message, kind: "console" }));
}
